import { QueryKey } from '@tanstack/react-query';
import { queryClient } from 'queryClient';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import {
  IntegrationEnum,
  IntegrationStatus,
  PropertySettingsResponseTO,
  InternalIntegrationSetting,
  IntegrationType,
} from 'models/InternalIntegrationSetting';
import { useTranslation } from 'react-i18next';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { BaseResponse } from 'models/_base';
import { INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY } from 'hooks/useFetchIntegrationPropertySettings';
import { getIntegrationAgencySettingsQueryKey } from 'hooks/useFetchIntegrationAgencySettings';
import { INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY } from 'pages/channels/integrationSettingsQuery';

export enum IntegrationMutationLevel {
  PROPERTY = 'property',
  AGENCY = 'agency',
}

type PropertyLevelParams = {
  level: IntegrationMutationLevel.PROPERTY;
  enumId: IntegrationEnum;
  propertyUids: string[];
  appliesToAllProperties: boolean;
  queryKey: QueryKey;
};

type AgencyLevelParams = {
  level: IntegrationMutationLevel.AGENCY;
  enumId: IntegrationEnum;
  queryKey: QueryKey;
};

type DisableIntegrationParams = PropertyLevelParams | AgencyLevelParams;

export interface CacheIntegrationAgencyLevelData extends BaseResponse {
  data: InternalIntegrationSetting[];
}

const useDisableIntegrationMutation = () => {
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();

  const { mutateAsync: disableIntegration, isLoading } = useAppMutation(
    (params: DisableIntegrationParams) => {
      const { enumId, level } = params;

      const payload =
        level === IntegrationMutationLevel.PROPERTY
          ? {
              propertyUids: params.propertyUids,
              appliesToAllProperties: params.appliesToAllProperties,
            }
          : {
              applyToAgency: true,
            };

      return API.post(
        `/api/internal/integrations/default/${enumId}/deactivate`,
        payload,
      );
    },
    {
      onMutate: async (params: DisableIntegrationParams) => {
        const { enumId, level, queryKey } = params;

        if (level === IntegrationMutationLevel.PROPERTY) {
          const { propertyUids, appliesToAllProperties } = params;

          const propertySettingsBaseKey = [
            INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
            INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY,
            'detail',
          ];

          await queryClient.cancelQueries({
            queryKey: propertySettingsBaseKey,
          });

          // Snapshot all property-settings queries for rollback
          const previousQueries =
            queryClient.getQueriesData<PropertySettingsResponseTO>({
              queryKey: propertySettingsBaseKey,
            });

          // Optimistically update all property-settings queries matching this enumId
          queryClient.setQueriesData<PropertySettingsResponseTO>(
            { queryKey: propertySettingsBaseKey },
            (cachedData) => {
              if (!cachedData || cachedData.enumId !== enumId) {
                return cachedData;
              }
              return {
                ...cachedData,
                propertySettings: cachedData.propertySettings.map((item) => {
                  // If the integration is not supported, we don't want to update the status
                  if (item.status === IntegrationStatus.NOT_SUPPORTED) {
                    return item;
                  }

                  // If the mutation is applied to all properties
                  if (appliesToAllProperties) {
                    return {
                      ...item,
                      status: IntegrationStatus.INACTIVE,
                    };
                  }

                  // If the mutation is applied to specific properties
                  if (propertyUids.includes(item.propertyUid)) {
                    return {
                      ...item,
                      status: IntegrationStatus.INACTIVE,
                    };
                  }

                  return item;
                }),
              };
            },
          );

          // Return a context object with the snapshotted value
          return { previousQueries, queryKey };
        }

        // Agency-level mutation
        await queryClient.cancelQueries({
          queryKey,
        });

        // Snapshot the previous value
        const previousData =
          queryClient.getQueryData<CacheIntegrationAgencyLevelData>(queryKey);

        // Optimistically update to the new value
        queryClient.setQueriesData<CacheIntegrationAgencyLevelData>(
          queryKey,
          (cachedData) => {
            if (!cachedData) return cachedData;
            return {
              ...cachedData,
              data: cachedData.data.map((item) => {
                if (item.enumId === enumId) {
                  return {
                    ...item,
                    agencySettings: {
                      ...item.agencySettings,
                      status: IntegrationStatus.INACTIVE,
                    },
                  };
                }

                return item;
              }),
            };
          },
        );

        // Return a context object with the snapshotted value
        return { previousData, queryKey };
      },
      onError: (error: any, __, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousQueries) {
          context.previousQueries.forEach(([key, data]) => {
            queryClient.setQueryData(key, data);
          });
        } else if (context?.previousData && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData);
        }

        notifyError(
          getErrorMessage({
            apiErrorCode: error.response.data?.apiErrorCode,
            apiErrorMessage: error.response.data?.apiErrorMessage,
            baseKey: 'pageIntegrations.apiErrorCodes.',
            t,
          }),
        );
      },
      onSettled: (_, __, params) => {
        // Always refetch after error or success to ensure we have the correct data
        const { level } = params;

        if (level === IntegrationMutationLevel.PROPERTY) {
          queryClient.invalidateQueries({
            queryKey: [
              INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
              INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY,
              'detail',
            ],
          });
          queryClient.removeQueries({
            queryKey: [INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY, 'list'],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: params.queryKey,
          });
          queryClient.invalidateQueries({
            queryKey: getIntegrationAgencySettingsQueryKey({
              enumId: params.enumId,
            }),
          });
        }
      },
      onSuccess: (_, variables) => {
        const { enumId, level, queryKey } = variables;

        let integrationType: IntegrationType | undefined;

        if (level === IntegrationMutationLevel.PROPERTY) {
          const matchingQueries =
            queryClient.getQueriesData<PropertySettingsResponseTO>({
              queryKey: [
                INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
                INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY,
                'detail',
              ],
            });
          const firstMatch = matchingQueries.find(
            ([, data]) => data?.enumId === enumId,
          );
          integrationType = firstMatch?.[1]?.integration?.type;
        } else {
          const cachedData =
            queryClient.getQueryData<CacheIntegrationAgencyLevelData>(queryKey);
          const integration = cachedData?.data.find(
            (item) => item.enumId === enumId,
          );
          integrationType = integration?.integration?.type;
        }

        const successMessageKey =
          integrationType === IntegrationType.CHANNEL
            ? 'pageChannels.disableChannelSuccess'
            : 'pageIntegrations.disableIntegrationSuccess';

        notifySuccess(t(successMessageKey));
      },
    },
  );

  return {
    disableIntegration,
    isLoading,
  };
};

export default useDisableIntegrationMutation;
